import { baseApi } from "@/shared/api";
import type {
  GuestExchangeResponse,
  GuestLocale,
  GuestMobileStartResponse,
  GuestMobileStatusResponse,
  GuestRefuseResponse,
  GuestSignResponse,
  GuestSigningDocument,
} from "../types/guestSigning";

const NONCE_HEADER = "X-Guest-Signing-Nonce";

function nonceHeaders(nonce: string): Record<string, string> {
  return { [NONCE_HEADER]: nonce };
}

export const guestSigningApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    exchangeGuestToken: builder.mutation<GuestExchangeResponse, { token: string; lang: GuestLocale }>({
      query: ({ token, lang }) => ({
        url: "/api/signing/public/exchange/",
        method: "POST",
        body: { token, lang },
        credentials: "include" as const,
      }),
    }),

    getGuestDocument: builder.query<GuestSigningDocument, GuestLocale>({
      query: (lang) => ({
        url: `/api/signing/public/document/?lang=${lang}`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "GuestSigning" as const, id: "DOCUMENT" }],
    }),

    signGuestDocument: builder.mutation<GuestSignResponse, { nonce: string; cms_signature: string }>({
      query: ({ nonce, cms_signature }) => ({
        url: "/api/signing/public/sign/",
        method: "POST",
        body: { cms_signature },
        headers: nonceHeaders(nonce),
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "GuestSigning" as const, id: "DOCUMENT" }],
    }),

    refuseGuestDocument: builder.mutation<GuestRefuseResponse, { nonce: string; reason: string }>({
      query: ({ nonce, reason }) => ({
        url: "/api/signing/public/refuse/",
        method: "POST",
        body: { reason },
        headers: nonceHeaders(nonce),
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "GuestSigning" as const, id: "DOCUMENT" }],
    }),

    startGuestMobileSigning: builder.mutation<GuestMobileStartResponse, { nonce: string }>({
      query: ({ nonce }) => ({
        url: "/api/signing/public/mobile/start/",
        method: "POST",
        headers: nonceHeaders(nonce),
        credentials: "include" as const,
      }),
    }),

    getGuestMobileStatus: builder.query<GuestMobileStatusResponse, string>({
      query: (operationId) => ({
        url: `/api/signing/public/mobile/status/${operationId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
    }),

    cancelGuestMobileSigning: builder.mutation<
      { operation_id: string; status: string },
      { nonce: string; operationId: string }
    >({
      query: ({ nonce, operationId }) => ({
        url: `/api/signing/public/mobile/cancel/${operationId}/`,
        method: "DELETE",
        headers: nonceHeaders(nonce),
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useExchangeGuestTokenMutation,
  useGetGuestDocumentQuery,
  useSignGuestDocumentMutation,
  useRefuseGuestDocumentMutation,
  useStartGuestMobileSigningMutation,
  useGetGuestMobileStatusQuery,
  useCancelGuestMobileSigningMutation,
} = guestSigningApi;
